import React, { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { fetchStoreBySlug } from '../store/reducers/homeReducer'
import Headers from '../components/Headers'
import { normalizeStoreName } from '../utils/storeUtils'
import Footer from '../components/Footer'

const AboutUs = () => {
    const { storeName } = useParams()
    const isStoreMode = !!storeName;
    const dispatch = useDispatch()
    const { seller } = useSelector(state => state.home)

    // Theme logic - Only if NOT StoreMode
    const getThemeClass = () => {
        if (isStoreMode) return '';

        const themeSettings = seller?.themeSettings;
        const useCustomTheme = themeSettings?.useCustomTheme;

        if (useCustomTheme) {
            return 'light-theme';
        }
        if (seller && seller.shopInfo) {
            return seller.shopInfo.theme === 'light' ? 'light-theme' : '';
        }
        if (storeName) {
            const normalizedStore = normalizeStoreName(storeName);
            const cachedTheme = localStorage.getItem(`store-theme-${normalizedStore}`);
            return cachedTheme === 'light' ? 'light-theme' : '';
        }
        return '';
    };
    const themeClass = getThemeClass();

    // Apply theme class to body immediately on render if we have cached theme context
    // Only if NOT StoreMode
    if (!isStoreMode && themeClass === 'light-theme') {
        document.body.classList.add('light-theme');

        // If we have cached custom theme settings, apply them now to prevent flash
        if (storeName) {
            try {
                const normalizedStore = normalizeStoreName(storeName);
                const cachedSettings = localStorage.getItem(`store-settings-${normalizedStore}`);
                if (cachedSettings) {
                    const settings = JSON.parse(cachedSettings);
                    if (settings.useCustomTheme && settings.colors) {
                        const colors = settings.colors;
                        const setVar = (name, value) => {
                            if (value) document.body.style.setProperty(name, value);
                        };
                        const getContrastColor = (hexcolor) => {
                            if (!hexcolor || hexcolor === 'transparent') return null;
                            const hex = hexcolor.replace('#', '');
                            const r = parseInt(hex.slice(0, 2), 16);
                            const g = parseInt(hex.slice(2, 4), 16);
                            const b = parseInt(hex.slice(4, 6), 16);
                            const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
                            return (yiq >= 128) ? '#000000' : '#ffffff';
                        };
                        const contrastColor = getContrastColor(colors.background);
                        setVar('--store-bg', colors.background);
                        setVar('--store-text', contrastColor);
                        setVar('--store-icon', contrastColor);
                        setVar('--store-button-bg', colors.buttonBg);
                        setVar('--store-button-text', colors.buttonText);
                        setVar('--store-product-card-bg', colors.productCardBg);
                        setVar('--store-header-bg', colors.headerBg);
                        setVar('--store-footer-bg', colors.footerBg);
                        setVar('--store-border', colors.border);
                        if (settings.typography?.fontFamily) {
                            setVar('--store-font-family', `'${settings.typography.fontFamily}', sans-serif`);
                        }
                    }
                }
            } catch (e) {
                console.error("Error applying cached theme:", e);
            }
        }
    }

    useEffect(() => {
        // Only fetch store if NOT in StoreLayout. StoreLayout handles it otherwise.
        if (storeName && !isStoreMode) {
            dispatch(fetchStoreBySlug(storeName))
        }
    }, [storeName, dispatch, isStoreMode])

    // Theme effect - Only if NOT StoreMode
    useEffect(() => {
        if (isStoreMode) return;

        if (seller && seller.shopInfo && storeName) {
            const normalizedStore = normalizeStoreName(storeName);
            localStorage.setItem(`store-theme-${normalizedStore}`, seller.shopInfo.theme);
            localStorage.setItem(`store-settings-${normalizedStore}`, JSON.stringify(seller.themeSettings || {}));

            const themeSettings = seller?.themeSettings;
            const useCustomTheme = themeSettings?.useCustomTheme;

            // Apply light-theme class when using custom theme or when theme is 'light'
            if (useCustomTheme || seller.shopInfo.theme === 'light') {
                document.body.classList.add('light-theme');
            } else {
                document.body.classList.remove('light-theme');
            }

            // Apply custom CSS variables when useCustomTheme is true
            if (useCustomTheme && themeSettings) {
                const colors = themeSettings.colors || {};
                const setVar = (name, value) => {
                    if (value) document.body.style.setProperty(name, value);
                };

                const getContrastColor = (hexcolor) => {
                    if (!hexcolor || hexcolor === 'transparent') return null;
                    const hex = hexcolor.replace('#', '');
                    const r = parseInt(hex.slice(0, 2), 16);
                    const g = parseInt(hex.slice(2, 4), 16);
                    const b = parseInt(hex.slice(4, 6), 16);
                    const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
                    return (yiq >= 128) ? '#000000' : '#ffffff';
                };

                const contrastColor = getContrastColor(colors.background);
                setVar('--store-bg', colors.background);
                setVar('--store-text', contrastColor);
                setVar('--store-icon', contrastColor);
                setVar('--store-button-bg', colors.buttonBg);
                setVar('--store-button-text', colors.buttonText);
                setVar('--store-product-card-bg', colors.productCardBg);
                setVar('--store-header-bg', colors.headerBg);
                setVar('--store-footer-bg', colors.footerBg);
                setVar('--store-input-bg', themeSettings.inputs?.background || colors.inputBg);
                setVar('--store-input-text', themeSettings.inputs?.text || colors.inputText);
                setVar('--store-input-border', themeSettings.inputs?.border || colors.inputBorder);
                setVar('--store-input-focus', themeSettings.inputs?.focusRing || '#000000');
                setVar('--store-input-radius', `${themeSettings.inputs?.radius || 20}px`);
                setVar('--store-select-bg', themeSettings.selects?.background || colors.inputBg);
                setVar('--store-select-text', themeSettings.selects?.text || colors.inputText);
                setVar('--store-select-border', themeSettings.selects?.border || colors.inputBorder);
                setVar('--store-select-focus', themeSettings.selects?.focusRing || '#000000');
                setVar('--store-select-radius', `${themeSettings.selects?.radius || 20}px`);
                setVar('--store-border', colors.border);
                setVar('--store-muted', colors.muted);
                setVar('--store-glass', colors.glass);

                // Load and apply Google Font
                if (themeSettings.typography?.fontFamily) {
                    const fontFamily = themeSettings.typography.fontFamily;
                    const fontId = `google-font-${fontFamily.replace(/\s+/g, '-')}`;

                    if (!document.getElementById(fontId)) {
                        const link = document.createElement('link');
                        link.id = fontId;
                        link.rel = 'stylesheet';
                        link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontFamily)}:wght@300;400;500;600;700&display=swap`;
                        document.head.appendChild(link);
                    }

                    setVar('--store-font-family', `"${fontFamily}", sans-serif`);
                    document.body.style.fontFamily = `"${fontFamily}", sans-serif`;
                }
            }
        }
        return () => {
            document.body.classList.remove('light-theme');
        };
    }, [seller, storeName, isStoreMode]);

    const content = storeName && seller?.footerInfo?.aboutUs
        ? seller.footerInfo.aboutUs
        : null

    return (
        <div className={`min-h-screen w-full transition-colors duration-300 ${!isStoreMode ? themeClass : ''} bg-[var(--store-bg)] text-[var(--store-text)]`}>
            {!isStoreMode && <Headers storeName={storeName} />}
            <div className='w-[85%] md:w-[95%] mx-auto py-16 md:py-8'>
                <div className={`rounded-lg p-8 md:p-4 bg-[var(--store-card-bg)]`}>
                    <h1 className='text-3xl md:text-2xl font-bold mb-6 text-[var(--store-text)]'>
                        {storeName ? `About ${seller?.shopInfo?.shopName || storeName}` : 'About Stores'}
                    </h1>

                    <div className='prose max-w-none text-[var(--store-muted)]'>
                        {content ? (
                            <div className="whitespace-pre-wrap text-[var(--store-text)]">
                                {content}
                            </div>
                        ) : (
                            <>
                                <h2 className='text-xl font-semibold mb-4 text-[var(--store-text)]'>Who We Are</h2>
                                <p className='mb-4 text-[var(--store-muted)]'>
                                    Stores is a leading e-commerce platform that connects buyers and sellers in a secure,
                                    user-friendly marketplace. We are committed to providing an exceptional online shopping
                                    experience for customers while empowering businesses to grow their online presence.
                                </p>

                                <h2 className='text-xl font-semibold mb-4 text-[var(--store-text)]'>Our Mission</h2>
                                <p className='mb-4 text-[var(--store-muted)]'>
                                    Our mission is to democratize e-commerce by providing accessible tools and platforms
                                    that enable anyone to sell online successfully. We strive to create a trusted marketplace
                                    where quality products meet satisfied customers.
                                </p>

                                <h2 className='text-xl font-semibold mb-4 text-[var(--store-text)]'>What We Offer</h2>
                                <ul className='list-disc pl-6 mb-4 text-[var(--store-muted)]'>
                                    <li>Wide variety of products from verified sellers</li>
                                    <li>Secure payment processing with multiple payment options</li>
                                    <li>Fast and reliable shipping</li>
                                    <li>24/7 customer support</li>
                                    <li>Easy returns and refunds</li>
                                    <li>Business tools for sellers to manage their stores</li>
                                </ul>

                                <h2 className='text-xl font-semibold mb-4 text-[var(--store-text)]'>Contact Information</h2>
                                <p className='text-[var(--store-muted)]'>
                                    <strong>Email:</strong> support@mypursepay.com<br />
                                    <strong>Website:</strong> www.stores.mypursepay.com
                                </p>
                            </>
                        )}
                    </div>
                </div>
            </div>
            {!isStoreMode && <Footer />}
        </div>
    )
}

export default AboutUs
